import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { repositionService } from "../services/reposition.service";
import { supabase } from "../lib/supabase";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Loader from "../components/Loader";

export default function RepositionRequest() {
  const navigate = useNavigate();
  const [reason, setReason] = useState<string>("");
  const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  
  // Datos del usuario cargados del localStorage
  const [userData, setUserData] = useState<any>(null);
  const [groupData, setGroupData] = useState<any>(null);
  const [existingParticipation, setExistingParticipation] = useState<any>(null);
  const [isRepositionClosed, setIsRepositionClosed] = useState(false);
  const [isCheckingParticipation, setIsCheckingParticipation] = useState(true);

  useEffect(() => {
    const init = async () => {
      const storedUserData = localStorage.getItem('userData');
      if (!storedUserData) {
        navigate('/');
        return;
      }
      
      try {
        const parsedData = JSON.parse(storedUserData);
        if (!parsedData.user || !parsedData.group) {
          navigate('/');
          return;
        }
        setUserData(parsedData.user);
        setGroupData(parsedData.group);

        // Check participation + whether reposition is already Completado (in_production)
        if (parsedData.group.id && parsedData.user.id) {
          const [participation, computedStatus] = await Promise.all([
            repositionService.getUserParticipation(parsedData.group.id, parsedData.user.id),
            repositionService.getComputedStatusForGroup(parsedData.group.id),
          ]);
          setExistingParticipation(participation);
          setIsRepositionClosed(computedStatus?.computed_key === 'in_production');
        }
      } catch (e) {
        navigate('/');
      } finally {
        setIsCheckingParticipation(false);
      }
    };

    init();
  }, [navigate]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(Array.from(files));
    }
    // Resetear el valor del input para permitir seleccionar los mismos archivos si se borraron
    e.target.value = '';
  };

  const processFiles = (files: File[]) => {
    const validFiles = files.filter(file => {
      // Validate file types (images and documents)
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type)) {
        toast.error(`Este tipo de archivo no está permitido: ${file.name}. Por favor, usa imágenes JPG, PNG, GIF, WebP o PDF.`);
        return false;
      }

      if (file.size > maxSize) {
        toast.error(`El archivo ${file.name} es muy grande. El tamaño máximo es de 5MB.`);
        return false;
      }

      return true;
    });

    setEvidenceFiles(prev => [...prev, ...validFiles]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const removeFile = (indexToRemove: number) => {
    setEvidenceFiles(files => files.filter((_, index) => index !== indexToRemove));
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return (
        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
        </svg>
      );
    } else if (fileType === 'application/pdf') {
      return (
        <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
        </svg>
      );
    } else {
      return (
        <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      );
    }
  };

  const uploadFilesToSupabase = async (files: File[]): Promise<string[]> => {
    const uploadedUrls: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileExt = file.name.split('.').pop();
      // Usar un nombre más único para evitar colisiones
      const uniqueId = Math.random().toString(36).substring(2, 15);
      const fileName = `${groupData.id}_evidence_${Date.now()}_${uniqueId}.${fileExt}`;

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('reposition-evidence')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw new Error(`Error al subir ${file.name}. Inténtalo de nuevo.`);
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('reposition-evidence')
        .getPublicUrl(fileName);

      uploadedUrls.push(publicUrl);

      // Update progress
      setUploadProgress(Math.round(((i + 1) / files.length) * 100));
    }

    return uploadedUrls;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userData?.id || !groupData?.id) {
      toast.error("No se pudo identificar tu pedido. Por favor, vuelve a iniciar sesión.");
      return;
    }

    if (!reason.trim()) {
      toast.error("Por favor, cuéntanos qué ha pasado con tu pedido");
      return;
    }

    setIsLoading(true);

    try {
      // Upload files to Supabase and get URLs
      let evidenceUrls = null;
      if (evidenceFiles.length > 0) {
        setUploadProgress(0);
        toast.loading("Subiendo tus archivos...");
        evidenceUrls = await uploadFilesToSupabase(evidenceFiles);
        toast.dismiss();
        toast.success("Archivos subidos correctamente");
      }

      // Call the reposition service
      const result = await repositionService.addOrderToReposition({
        group_id: groupData.id,
        order_id: userData.id,
        reason: reason.trim(),
        evidence_urls: evidenceUrls,
      });

      if (!result.success) {
        toast.error(result.error || "No se pudo enviar tu reporte. Por favor, inténtalo de nuevo.");
        return;
      }

      toast.success(
        "¡Reporte enviado! Nos pondremos en contacto contigo pronto para solucionar el problema.",
        { duration: 5000 }
      );

      // Refrescar la página para mostrar datos actualizados
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      toast.dismiss(); // Dismiss any loading toasts

      if (error instanceof Error && error.message.includes('uploading')) {
        toast.error(`Error al subir las fotos: ${error.message}`);
      } else {
        toast.error("Ocurrió un error al enviar tu reporte. Por favor, inténtalo de nuevo.");
      }
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  if (!userData || !groupData || isCheckingParticipation) {
    return <Loader />;
  }



  // Si la reposición ya está Completada (en producción), no permitir nuevas solicitudes
  if (isRepositionClosed || !existingParticipation) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-grow max-w-3xl mx-auto w-full px-4 py-8 sm:px-6 lg:px-8 mt-[80px]">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-green-50 p-6 border-b border-green-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-green-100 p-2 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Reposición en producción</h1>
              </div>
              <p className="text-green-800">
                La reposición se encuentra en producción, el plazo para añadir nuevas solicitudes ha sido cerrado. Pongase en contacto con el comercial si tiene más dudas.
              </p>
            </div>

            <div className="p-6">
              <div className="flex justify-center">
                <button
                  onClick={() => navigate('/')}
                  className="text-gray-500 hover:text-gray-700 text-sm font-medium flex items-center gap-1 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  Volver al inicio
                </button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Si ya existe participación, mostrar estado en lugar de formulario
  if (existingParticipation) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-grow max-w-3xl mx-auto w-full px-4 py-8 sm:px-6 lg:px-8 mt-[80px]">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-green-50 p-6 border-b border-green-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-green-100 p-2 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Reporte enviado correctamente</h1>
              </div>
              <p className="text-green-800">
                Tu solicitud de reposición ya ha sido registrada y está siendo procesada.
              </p>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Detalles de tu reporte</h3>
                
                <div className="space-y-4">
                  <div>
                    <span className="block text-xs font-medium text-gray-500 mb-1">Motivo</span>
                    <p className="text-sm text-gray-800 bg-white p-3 rounded border border-gray-200">
                      {existingParticipation.reason}
                    </p>
                  </div>

                  {existingParticipation.evidence_urls && existingParticipation.evidence_urls.length > 0 && (
                    <div>
                      <span className="block text-xs font-medium text-gray-500 mb-2">Archivos adjuntos</span>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {existingParticipation.evidence_urls.map((url: string, index: number) => (
                          <a 
                            key={index}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block group relative aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200 hover:border-amber-500 transition-all"
                          >
                            {url.toLowerCase().match(/\.(jpeg|jpg|gif|png|webp)$/) ? (
                              <img 
                                src={url} 
                                alt={`Evidencia ${index + 1}`} 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                              <span className="opacity-0 group-hover:opacity-100 bg-white px-2 py-1 rounded text-xs font-medium shadow-sm">Ver</span>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="pt-2 border-t border-gray-100 mt-2">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Enviado el: {new Date(existingParticipation.created_at).toLocaleDateString()}</span>
                      {existingParticipation.status ? (
                        <span 
                          className="px-2 py-0.5 rounded-full font-medium"
                          style={{
                            backgroundColor: `${existingParticipation.status.color}20`, // 20% opacity
                            color: existingParticipation.status.color
                          }}
                        >
                          {existingParticipation.status.name}
                        </span>
                      ) : (
                        <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-medium">
                          Pendiente
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={() => navigate('/')}
                  className="text-gray-500 hover:text-gray-700 text-sm font-medium flex items-center gap-1 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  Volver al inicio
                </button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <main className="flex-grow max-w-3xl mx-auto w-full px-4 py-8 sm:px-6 lg:px-8 mt-[80px]">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Header del formulario */}
          <div className="bg-amber-50 p-6 border-b border-amber-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-amber-100 p-2 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-amber-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Reportar problema con pedido</h1>
            </div>
            <p className="text-amber-800">
              Estás reportando un problema para el grupo <span className="font-semibold">{groupData.name}</span>. 
              Por favor, detalla lo sucedido para que podamos ayudarte.
            </p>
          </div>

          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Info del pedido */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                  </svg>
                  Detalles del pedido
                </h3>
                <div className="text-sm text-gray-600 grid grid-cols-2 gap-4">
                  <div>
                    <span className="block text-xs text-gray-500">Nombre</span>
                    {userData.name}
                  </div>
                  <div>
                    <span className="block text-xs text-gray-500">Email</span>
                    {userData.email}
                  </div>
                </div>
              </div>

              {/* Reason */}
              <div>
                <label
                  htmlFor="reason"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  ¿Qué ha pasado? <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={5}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                  placeholder="Describe el problema con el mayor detalle posible (ej: talla incorrecta, defecto en el producto, pedido incompleto...)"
                  required
                />
              </div>

              {/* Evidence Files */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fotos o documentos (opcional)
                </label>
                
                <div className="relative">
                  <input
                    type="file"
                    id="evidence"
                    multiple
                    accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,text/plain"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    disabled={isLoading}
                  />
                  <div
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
                      isDragOver
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-300 hover:border-amber-400 hover:bg-amber-50/30'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className={`p-3 rounded-full transition-colors ${
                        isDragOver ? 'bg-amber-200' : 'bg-gray-100'
                      }`}>
                        <svg
                          className={`w-8 h-8 transition-colors ${
                            isDragOver ? 'text-amber-600' : 'text-gray-400'
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-700">
                          {isDragOver
                            ? 'Suelta los archivos aquí'
                            : <>Arrastra archivos o <span className="text-amber-600 underline font-semibold">haz clic para seleccionar</span></>}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          JPG, PNG, GIF, WebP o PDF (máx. 5MB)
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* File List */}
                {evidenceFiles.length > 0 && (
                  <div className="mt-4 bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                      <span className="text-xs font-semibold text-gray-700">Archivos seleccionados</span>
                      <span className="text-xs text-gray-500">{evidenceFiles.length} archivo(s)</span>
                    </div>
                    <ul className="divide-y divide-gray-100">
                      {evidenceFiles.map((file, index) => (
                        <li key={index} className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors">
                          <div className="flex items-center space-x-3 overflow-hidden">
                            <div className="flex-shrink-0">
                              {getFileIcon(file.type)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="flex-shrink-0 ml-4 p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            disabled={isLoading}
                            title="Eliminar archivo"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Progress Bar */}
                {isLoading && uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Subiendo archivos...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className="bg-amber-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="pt-4 border-t border-gray-200 flex gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="flex-1 px-6 py-3 text-sm text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !userData?.id || !reason.trim()}
                  className="flex-[2] px-6 py-3 text-sm bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg transition-all shadow-md hover:shadow-lg disabled:bg-gray-300 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Enviando reporte...
                    </>
                  ) : (
                    <>
                      Enviar reporte
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
